// src/pages/ReviewsPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Star, Edit3, Trash2, Package, Calendar, CheckCircle,
    AlertCircle, Clock, ThumbsUp, MessageSquare, ArrowLeft,
    Filter, Search, X, Image as ImageIcon
} from 'lucide-react';
import { getUserReviews, deleteReview, updateReview } from '../services/reviewService';
import { getAllProducts } from '../services/productService';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useSearch } from '../context/SearchContext';
import { openCartSidebar } from '../components/CartSidebar';
import toast from 'react-hot-toast';

export default function ReviewsPage() {
    const navigate = useNavigate();
    const [reviews, setReviews] = useState([]);
    const [filteredReviews, setFilteredReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterRating, setFilterRating] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [editingReview, setEditingReview] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [products, setProducts] = useState([]);
    const [availableCategories, setAvailableCategories] = useState([]);

    const { cartCount } = useCart();
    const { wishlistCount } = useWishlist();
    const { searchQuery, setSearchQuery } = useSearch();

    const userId = localStorage.getItem('userToken') || localStorage.getItem('userId');

    useEffect(() => {
        if (!userId) {
            toast.error('Lütfen giriş yapın');
            navigate('/account');
            return;
        }
        fetchReviews();
        fetchProducts();
    }, [userId, navigate]);

    const fetchProducts = async () => {
        try {
            const result = await getAllProducts();
            if (result.success) {
                const activeProducts = result.products.filter(product =>
                    product.isActive !== false
                );
                setProducts(activeProducts);

                // Mevcut kategorileri tespit et
                const categoriesSet = new Set();
                activeProducts.forEach(product => {
                    if (product.category) {
                        const mainCat = product.category.includes(' > ')
                            ? product.category.split(' > ')[0]
                            : product.category;
                        categoriesSet.add(mainCat);
                    }
                });
                setAvailableCategories(Array.from(categoriesSet));
            }
        } catch (error) {
            console.error('Ürünler yüklenemedi:', error);
        }
    };

    useEffect(() => {
        applyFilters();
    }, [reviews, filterRating, searchTerm]);

    const fetchReviews = async () => {
        console.log('🔍 Fetching reviews for userId:', userId);
        setLoading(true);
        const result = await getUserReviews(userId);
        console.log('📊 Review result:', result);
        if (result.success) {
            console.log('✅ Reviews loaded:', result.reviews.length);
            setReviews(result.reviews);
        } else {
            console.error('❌ Error loading reviews:', result.error);
            toast.error('Değerlendirmeler yüklenemedi');
        }
        setLoading(false);
    };

    const applyFilters = () => {
        let filtered = [...reviews];

        if (filterRating !== 'all') {
            filtered = filtered.filter(r => r.rating === parseInt(filterRating));
        }

        if (searchTerm.trim()) {
            filtered = filtered.filter(r =>
                r.productName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                r.comment?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                r.title?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        setFilteredReviews(filtered);
    };

    const handleDelete = async (reviewId) => {
        if (!window.confirm('Bu değerlendirmeyi silmek istediğinizden emin misiniz?')) return;

        const result = await deleteReview(reviewId, userId);
        if (result.success) {
            toast.success('Değerlendirme silindi');
            fetchReviews();
        } else {
            toast.error(result.error || 'Silme işlemi başarısız');
        }
    };

    const handleEdit = (review) => {
        setEditingReview({ ...review });
        setShowEditModal(true);
    };

    const handleUpdateReview = async () => {
        if (!editingReview.comment.trim()) {
            toast.error('Yorum boş olamaz');
            return;
        }

        const result = await updateReview(editingReview.id, userId, {
            rating: editingReview.rating,
            title: editingReview.title,
            comment: editingReview.comment
        });

        if (result.success) {
            toast.success('Değerlendirme güncellendi');
            setShowEditModal(false);
            fetchReviews();
        } else {
            toast.error(result.error || 'Güncelleme başarısız');
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
        }
    };

    const stats = {
        total: reviews.length,
        avgRating: reviews.length > 0
            ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
            : 0,
        verified: reviews.filter(r => r.isVerifiedPurchase).length,
        helpful: reviews.reduce((sum, r) => sum + (r.helpfulCount || 0), 0)
    };

    return (
        <div className="reviews-page">
            <Style />
            
            <Header
                wishlistCount={wishlistCount}
                cartCount={cartCount}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                onSearch={handleSearch}
                onCartClick={openCartSidebar}
                onWishlistClick={() => navigate('/wishlist')}
                onCampaignsClick={() => navigate('/campaigns')}
                availableCategories={availableCategories}
                products={products}
            />

            <div className="reviews-hero">
                <div className="container">
                    <div className="hero-content">
                        <div className="hero-text">
                            <h1>Değerlendirmelerim</h1>
                            <p>Ürünler hakkındaki görüşleriniz</p>
                        </div>
                        <div className="hero-stats">
                            <div className="stat-card">
                                <div className="stat-icon">
                                    <Star size={24} />
                                </div>
                                <div className="stat-info">
                                    <div className="stat-value">{stats.total}</div>
                                    <div className="stat-label">Değerlendirme</div>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-icon rating">
                                    <Star size={24} fill="currentColor" />
                                </div>
                                <div className="stat-info">
                                    <div className="stat-value">{stats.avgRating}</div>
                                    <div className="stat-label">Ortalama Puan</div>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-icon verified">
                                    <CheckCircle size={24} />
                                </div>
                                <div className="stat-info">
                                    <div className="stat-value">{stats.verified}</div>
                                    <div className="stat-label">Doğrulanmış</div>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-icon helpful">
                                    <ThumbsUp size={24} />
                                </div>
                                <div className="stat-info">
                                    <div className="stat-value">{stats.helpful}</div>
                                    <div className="stat-label">Faydalı</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="reviews-content">
                <div className="container">
                    <div className="filters-bar">
                        <div className="search-box">
                            <Search size={20} />
                            <input
                                type="text"
                                placeholder="Ürün veya yorum ara..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button className="clear-search" onClick={() => setSearchTerm('')}>
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                        <div className="filter-buttons">
                            <button
                                className={`filter-btn ${filterRating === 'all' ? 'active' : ''}`}
                                onClick={() => setFilterRating('all')}
                            >
                                Tümü
                            </button>
                            {[5, 4, 3, 2, 1].map(rating => (
                                <button
                                    key={rating}
                                    className={`filter-btn ${filterRating === rating.toString() ? 'active' : ''}`}
                                    onClick={() => setFilterRating(rating.toString())}
                                >
                                    <Star size={14} fill={filterRating === rating.toString() ? '#FFD700' : 'none'} />
                                    {rating}
                                </button>
                            ))}
                        </div>
                    </div>

                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Değerlendirmeler yükleniyor...</p>
                        </div>
                    ) : filteredReviews.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">
                                <MessageSquare size={64} />
                            </div>
                            <h3>Henüz değerlendirme yapmadınız</h3>
                            <p>Satın aldığınız ürünleri değerlendirerek diğer müşterilere yardımcı olabilirsiniz</p>
                            <button className="cta-btn" onClick={() => navigate('/orders')}>
                                Siparişlerime Git
                            </button>
                        </div>
                    ) : (
                        <div className="reviews-list">
                            {filteredReviews.map(review => (
                                <ReviewCard
                                    key={review.id}
                                    review={review}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <Footer />

            {showEditModal && (
                <EditModal
                    review={editingReview}
                    setReview={setEditingReview}
                    onSave={handleUpdateReview}
                    onClose={() => setShowEditModal(false)}
                />
            )}
        </div>
    );
}

function ReviewCard({ review, onEdit, onDelete }) {
    const navigate = useNavigate();
    const [imageError, setImageError] = useState({});

    const getStatusInfo = () => {
        switch (review.status) {
            case 'active':
                return { icon: CheckCircle, text: 'Yayında', color: '#06d6a0' };
            case 'pending':
                return { icon: Clock, text: 'İnceleniyor', color: '#FFB800' };
            case 'rejected':
                return { icon: AlertCircle, text: 'Reddedildi', color: '#E5332A' };
            default:
                return { icon: CheckCircle, text: 'Yayında', color: '#06d6a0' };
        }
    };

    const status = getStatusInfo();
    const StatusIcon = status.icon;

    return (
        <div className="review-card">
            <div className="review-header">
                <div className="product-info" onClick={() => navigate(`/product/${review.productId}`)}>
                    <Package size={20} />
                    <span className="product-name">{review.productName}</span>
                </div>
                <div className="review-meta">
                    <div className="status-badge" style={{ color: status.color }}>
                        <StatusIcon className='status-icon' size={14} />
                        <span className="status-text">{status.text}</span>
                    </div>
                    <div className="review-date">
                        <Calendar size={14} />
                        {new Date(review.createdAt).toLocaleDateString('tr-TR')}
                    </div>
                </div>
            </div>

            <div className="review-body">
                <div className="rating-display">
                    {[1, 2, 3, 4, 5].map(star => (
                        <Star
                            key={star}
                            size={20}
                            fill={star <= review.rating ? '#FFD700' : 'none'}
                            color={star <= review.rating ? '#FFD700' : '#ddd'}
                        />
                    ))}
                    {review.isVerifiedPurchase && (
                        <span className="verified-badge">
                            <CheckCircle size={14} />
                            Doğrulanmış Alışveriş
                        </span>
                    )}
                </div>

                {review.title && <h4 className="review-title">{review.title}</h4>}

                <p className="review-comment">{review.comment}</p>

                {review.images && review.images.length > 0 && (
                    <div className="review-images">
                        {review.images.map((img, idx) => (
                            <div key={idx} className="review-image">
                                {!imageError[idx] ? (
                                    <img
                                        src={img}
                                        alt={`Değerlendirme ${idx + 1}`}
                                        onError={() => setImageError({ ...imageError, [idx]: true })}
                                    />
                                ) : (
                                    <div className="image-placeholder">
                                        <ImageIcon size={24} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <div className="review-stats">
                    <div className="stat-item">
                        <ThumbsUp size={14} />
                        <span>{review.helpfulCount || 0} kişi faydalı buldu</span>
                    </div>
                </div>

                {review.adminReply && (
                    <div className="admin-reply">
                        <div className="reply-header">
                            <MessageSquare size={16} />
                            <span>Mağaza Yanıtı</span>
                        </div>
                        <p>{review.adminReply.message}</p>
                    </div>
                )}
            </div>

            <div className="review-actions">
                <button className="action-btn edit" onClick={() => onEdit(review)}>
                    <Edit3 size={16} />
                    Düzenle
                </button>
                <button className="action-btn delete" onClick={() => onDelete(review.id)}>
                    <Trash2 size={16} />
                    Sil
                </button>
            </div>
        </div>
    );
}

function EditModal({ review, setReview, onSave, onClose }) {
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Değerlendirmeyi Düzenle</h3>
                    <button className="close-btn" onClick={onClose}>
                        <X size={24} />
                    </button>
                </div>

                <div className="modal-body">
                    <div className="form-group">
                        <label>Puanınız</label>
                        <div className="star-rating">
                            {[1, 2, 3, 4, 5].map(star => (
                                <button
                                    key={star}
                                    type="button"
                                    className="star-btn"
                                    onClick={() => setReview({ ...review, rating: star })}
                                >
                                    <Star
                                        size={32}
                                        fill={star <= review.rating ? '#FFD700' : 'none'}
                                        color={star <= review.rating ? '#FFD700' : '#ddd'}
                                    />
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Başlık (Opsiyonel)</label>
                        <input
                            type="text"
                            placeholder="Değerlendirmenize başlık ekleyin"
                            value={review.title || ''}
                            onChange={(e) => setReview({ ...review, title: e.target.value })}
                        />
                    </div>

                    <div className="form-group">
                        <label>Yorumunuz *</label>
                        <textarea
                            placeholder="Ürün hakkındaki görüşlerinizi paylaşın..."
                            value={review.comment}
                            onChange={(e) => setReview({ ...review, comment: e.target.value })}
                            rows={6}
                        />
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="cancel-btn" onClick={onClose}>
                        İptal
                    </button>
                    <button className="save-btn" onClick={onSave}>
                        Kaydet
                    </button>
                </div>
            </div>
        </div>
    );
}

function Style() {
    return <style>{`
    /* ==================== GLOBAL STYLES ==================== */
    * { 
        margin: 0; 
        padding: 0; 
        box-sizing: border-box; 
        -webkit-tap-highlight-color: transparent; 
    }
    
    html { 
        scroll-behavior: smooth; 
    }
    
    body { 
        font-family: 'Inter', -apple-system, sans-serif; 
        background: #f8f9fa; 
        color: #2B2B2B; 
    }
    
    img { 
        max-width: 100%; 
        height: auto; 
    }

    /* ==================== CONTAINER (HomePage ile aynı) ==================== */
    .container { 
        max-width: 1320px; 
        margin: 0 auto; 
        padding: 0 20px; 
    }

    /* ==================== PAGE WRAPPER ==================== */
    .reviews-page {
        min-height: 100vh;
        background: #f8f9fa;
    }

    /* ==================== HERO SECTION ==================== */
    .reviews-hero {
        background: linear-gradient(135deg, #2B2B2B 0%, #1a1a1a 100%);
        padding: 32px 0 64px;
        position: relative;
        overflow: hidden;
    }

    .reviews-hero::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: 
            radial-gradient(circle at 20% 50%, rgba(229, 51, 42, 0.1), transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(6, 214, 160, 0.08), transparent 50%);
        pointer-events: none;
    }

    .hero-content {
        display: grid;
        grid-template-columns: 1fr 2fr;
        gap: 40px;
        align-items: center;
    }

    .hero-text h1 {
        font-size: 48px;
        font-weight: 800;
        color: #fff;
        margin-bottom: 12px;
        letter-spacing: -0.5px;
    }

    .hero-text p {
        font-size: 18px;
        color: rgba(255, 255, 255, 0.7);
    }

    .hero-stats {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 16px;
    }

    .stat-card {
        background: rgba(255, 255, 255, 0.05);
        backdrop-filter: blur(20px);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 16px;
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: 12px;
        transition: all 0.3s ease;
    }

    .stat-card:hover {
        transform: translateY(-4px);
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 255, 255, 0.2);
    }

    .stat-icon {
        width: 48px;
        height: 48px;
        background: linear-gradient(135deg, #E5332A, #ff4436);
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
    }

    .stat-icon.rating {
        background: linear-gradient(135deg, #FFD700, #FFA500);
    }

    .stat-icon.verified {
        background: linear-gradient(135deg, #06d6a0, #00b894);
    }

    .stat-icon.helpful {
        background: linear-gradient(135deg, #5f27cd, #341f97);
    }

    .stat-info {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .stat-value {
        font-size: 28px;
        font-weight: 800;
        color: #fff;
        line-height: 1;
    }

    .stat-label {
        font-size: 13px;
        color: rgba(255, 255, 255, 0.6);
        font-weight: 500;
    }

    /* ==================== CONTENT SECTION ==================== */
    .reviews-content {
        padding: 40px 0 80px;
        margin-top: -48px;
        position: relative;
        z-index: 2;
    }

    /* ==================== FILTERS BAR ==================== */
    .filters-bar {
        background: #fff;
        border-radius: 16px;
        padding: 20px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        margin-bottom: 32px;
        display: flex;
        gap: 16px;
        align-items: center;
        flex-wrap: wrap;
    }

    .search-box {
        flex: 1;
        min-width: 300px;
        position: relative;
        display: flex;
        align-items: center;
        background: #f8f9fa;
        border: 2px solid #e9ecef;
        border-radius: 12px;
        padding: 12px 16px;
        gap: 12px;
        transition: all 0.2s;
    }

    .search-box:focus-within {
        border-color: #E5332A;
        background: #fff;
        box-shadow: 0 0 0 3px rgba(229, 51, 42, 0.1);
    }

    .search-box svg {
        color: #868e96;
        flex-shrink: 0;
    }

    .search-box input {
        flex: 1;
        border: none;
        background: transparent;
        font-size: 14px;
        outline: none;
        color: #2B2B2B;
    }

    .search-box input::placeholder {
        color: #adb5bd;
    }

    .clear-search {
        background: none;
        border: none;
        cursor: pointer;
        color: #868e96;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 4px;
        border-radius: 50%;
        transition: all 0.2s;
    }

    .clear-search:hover {
        background: #e9ecef;
        color: #E5332A;
    }

    .filter-buttons {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
    }

    .filter-btn {
        background: #f8f9fa;
        border: 2px solid #e9ecef;
        color: #495057;
        padding: 10px 16px;
        border-radius: 10px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
    }

    .filter-btn:hover {
        background: #fff;
        border-color: #E5332A;
        color: #E5332A;
    }

    .filter-btn.active {
        background: #E5332A;
        border-color: #E5332A;
        color: #fff;
    }

    /* ==================== REVIEWS LIST ==================== */
    .reviews-list {
        display: grid;
        gap: 20px;
    }

    .review-card {
        background: #fff;
        border-radius: 16px;
        padding: 24px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        transition: all 0.3s ease;
        border: 2px solid transparent;
    }

    .review-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
        border-color: #E5332A;
    }

    .review-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 20px;
        padding-bottom: 16px;
        border-bottom: 1px solid #e9ecef;
    }

    .product-info {
        display: flex;
        align-items: center;
        gap: 10px;
        cursor: pointer;
        transition: all 0.2s;
        padding: 8px 12px;
        border-radius: 8px;
        background: #f8f9fa;
    }

    .product-info:hover {
        background: #e9ecef;
        color: #E5332A;
    }

    .product-info svg {
        flex-shrink: 0;
    }

    .product-name {
        font-weight: 600;
        font-size: 15px;
    }

    .review-meta {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .status-badge {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px 12px;
        border-radius: 8px;
        font-weight: 600;
        font-size: 12px;
        background: currentColor;
        color: #fff;
        opacity: 0.9;
    }

    .status-text {
        color: #fff;
        font-weight: 500;
    }

    .status-icon {
        color: #fff;
    }

    .review-date {
        display: flex;
        align-items: center;
        gap: 6px;
        color: #868e96;
        font-size: 13px;
    }

    .review-body {
        margin-bottom: 20px;
    }

    .rating-display {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 12px;
    }

    .verified-badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: #06d6a0;
        color: #fff;
        padding: 4px 10px;
        border-radius: 6px;
        font-size: 11px;
        font-weight: 600;
        margin-left: 8px;
    }

    .review-title {
        font-size: 16px;
        font-weight: 700;
        color: #2B2B2B;
        margin-bottom: 10px;
    }

    .review-comment {
        font-size: 14px;
        line-height: 1.7;
        color: #495057;
        margin-bottom: 16px;
    }

    .review-images {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
        gap: 10px;
        margin-bottom: 16px;
        max-width: 400px;
    }

    .review-image {
        aspect-ratio: 1;
        border-radius: 10px;
        overflow: hidden;
        background: #f8f9fa;
        cursor: pointer;
        transition: transform 0.3s;
    }

    .review-image:hover {
        transform: scale(1.05);
    }

    .review-image img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .image-placeholder {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #adb5bd;
    }

    .review-stats {
        display: flex;
        gap: 16px;
        margin-top: 12px;
    }

    .stat-item {
        display: flex;
        align-items: center;
        gap: 6px;
        color: #868e96;
        font-size: 13px;
    }

    .admin-reply {
        background: #f8f9fa;
        border-left: 4px solid #E5332A;
        border-radius: 10px;
        padding: 16px;
        margin-top: 16px;
    }

    .reply-header {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #E5332A;
        font-weight: 700;
        margin-bottom: 10px;
        font-size: 13px;
    }

    .admin-reply p {
        color: #495057;
        line-height: 1.6;
        font-size: 14px;
    }

    .review-actions {
        display: flex;
        gap: 10px;
        padding-top: 16px;
        border-top: 1px solid #e9ecef;
    }

    .action-btn {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 12px 20px;
        border-radius: 10px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
        border: 2px solid #e9ecef;
        background: #fff;
        color: #495057;
        font-size: 14px;
    }

    .action-btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .action-btn.edit {
        border-color: #5f27cd;
        color: #5f27cd;
    }

    .action-btn.edit:hover {
        background: #5f27cd;
        color: #fff;
    }

    .action-btn.delete {
        border-color: #E5332A;
        color: #E5332A;
    }

    .action-btn.delete:hover {
        background: #E5332A;
        color: #fff;
    }

    /* ==================== MODAL ==================== */
    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(8px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        padding: 20px;
        animation: fadeIn 0.3s ease;
    }

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }

    .modal-content {
        background: #fff;
        border-radius: 20px;
        max-width: 600px;
        width: 100%;
        max-height: 90vh;
        overflow-y: auto;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        animation: slideUp 0.3s ease;
    }

    @keyframes slideUp {
        from {
            opacity: 0;
            transform: translateY(30px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 24px 24px 20px;
        border-bottom: 1px solid #e9ecef;
    }

    .modal-header h3 {
        font-size: 20px;
        font-weight: 700;
        color: #2B2B2B;
    }

    .close-btn {
        background: #f8f9fa;
        border: none;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s;
    }

    .close-btn:hover {
        background: #E5332A;
        color: #fff;
        transform: rotate(90deg);
    }

    .modal-body {
        padding: 24px;
    }

    .form-group {
        margin-bottom: 20px;
    }

    .form-group label {
        display: block;
        font-weight: 600;
        font-size: 14px;
        color: #2B2B2B;
        margin-bottom: 10px;
    }

    .star-rating {
        display: flex;
        gap: 8px;
    }

    .star-btn {
        background: none;
        border: none;
        cursor: pointer;
        padding: 0;
        transition: transform 0.2s;
    }

    .star-btn:hover {
        transform: scale(1.2);
    }

    .form-group input,
    .form-group textarea {
        width: 100%;
        padding: 12px 16px;
        border: 2px solid #e9ecef;
        border-radius: 12px;
        font-size: 14px;
        outline: none;
        transition: all 0.2s;
        font-family: inherit;
    }

    .form-group input:focus,
    .form-group textarea:focus {
        border-color: #E5332A;
        box-shadow: 0 0 0 3px rgba(229, 51, 42, 0.1);
    }

    .form-group textarea {
        resize: vertical;
        min-height: 120px;
    }

    .modal-footer {
        display: flex;
        gap: 12px;
        padding: 20px 24px 24px;
        border-top: 1px solid #e9ecef;
    }

    .cancel-btn,
    .save-btn {
        flex: 1;
        padding: 14px;
        border-radius: 12px;
        font-weight: 700;
        font-size: 15px;
        cursor: pointer;
        transition: all 0.2s;
        border: none;
    }

    .cancel-btn {
        background: #f8f9fa;
        color: #495057;
    }

    .cancel-btn:hover {
        background: #e9ecef;
    }

    .save-btn {
        background: #E5332A;
        color: #fff;
    }

    .save-btn:hover {
        background: #c72a22;
        transform: translateY(-1px);
    }

    /* ==================== EMPTY STATE ==================== */
    .empty-state {
        background: #fff;
        border-radius: 20px;
        padding: 60px 40px;
        text-align: center;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    }

    .empty-icon {
        width: 100px;
        height: 100px;
        background: #f8f9fa;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 24px;
        color: #adb5bd;
    }

    .empty-state h3 {
        font-size: 24px;
        font-weight: 700;
        color: #2B2B2B;
        margin-bottom: 10px;
    }

    .empty-state p {
        font-size: 15px;
        color: #868e96;
        margin-bottom: 24px;
        line-height: 1.6;
    }

    .cta-btn {
        background: #E5332A;
        color: #fff;
        border: none;
        padding: 14px 28px;
        border-radius: 12px;
        font-weight: 700;
        font-size: 15px;
        cursor: pointer;
        transition: all 0.2s;
    }

    .cta-btn:hover {
        background: #c72a22;
        transform: translateY(-1px);
    }

    /* ==================== LOADING STATE ==================== */
    .loading-state {
        text-align: center;
        padding: 80px 20px;
    }

    .spinner {
        width: 48px;
        height: 48px;
        border: 4px solid #e9ecef;
        border-top-color: #E5332A;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto 16px;
    }

    @keyframes spin {
        to { transform: rotate(360deg); }
    }

    .loading-state p {
        font-size: 14px;
        color: #868e96;
        font-weight: 600;
    }

    /* ==================== RESPONSIVE ==================== */
    @media (max-width: 992px) {
        .hero-content {
            grid-template-columns: 1fr;
            gap: 32px;
        }

        /* ✅ 992px - Tablet'te 2x2 grid */
        .hero-stats {
            grid-template-columns: repeat(2, 1fr);
        }

        .hero-text h1 {
            font-size: 36px;
        }
    }

    @media (max-width: 768px) {
        .container {
            padding: 0 16px;
        }

        .reviews-hero {
            padding: 20px 0 48px;
        }

        .hero-text h1 {
            font-size: 28px;
        }

        .hero-text p {
            font-size: 16px;
        }

        /* ✅ 768px - Mobilde 2x2 grid */
        .hero-stats {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
        }

        .stat-card {
            padding: 16px;
        }

        .stat-icon {
            width: 42px;
            height: 42px;
        }

        .stat-value {
            font-size: 24px;
        }

        .stat-label {
            font-size: 12px;
        }

        .filters-bar {
            flex-direction: column;
            align-items: stretch;
            padding: 16px;
        }

        .search-box {
            width: 100%;
        }

        .filter-buttons {
            justify-content: center;
            flex-wrap: nowrap;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            -ms-overflow-style: none;
            width: 100%;
        }

        .filter-buttons::-webkit-scrollbar {
            display: none;
        }

        .filter-btn {
            flex-shrink: 0;
            white-space: nowrap;
        }

        .review-card {
            padding: 16px;
        }

        .review-header {
            flex-direction: column;
            gap: 12px;
        }

        .review-meta {
            width: 100%;
            justify-content: space-between;
        }

        .review-actions {
            flex-direction: column;
        }

        .modal-content {
            margin: 20px;
        }

        .modal-header,
        .modal-body,
        .modal-footer {
            padding: 16px;
        }
    }

    @media (max-width: 480px) {
        .container {
            padding: 0 12px;
        }

        .search-box {
            width: 100%;
        }

        .filter-buttons {
            width: 100%;
        }

        .reviews-hero {
            padding: 16px 0 40px;
        }

        .hero-text h1 {
            font-size: 24px;
        }

        .hero-text p {
            font-size: 14px;
        }

        /* ✅ 480px - Küçük mobilde de 2x2 grid */
        .hero-stats {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
        }

        .stat-card {
            padding: 14px;
        }

        .stat-icon {
            width: 38px;
            height: 38px;
        }

        .stat-value {
            font-size: 20px;
        }

        .stat-label {
            font-size: 11px;
        }

        .filters-bar {
            padding: 14px;
        }

        .filter-btn {
            padding: 8px 14px;
            font-size: 13px;
        }

        .review-card {
            padding: 14px;
        }

        .product-name {
            font-size: 14px;
        }

        .review-title {
            font-size: 15px;
        }

        .review-comment {
            font-size: 13px;
        }

        .action-btn {
            padding: 10px 16px;
            font-size: 13px;
        }

        .empty-state {
            padding: 40px 20px;
        }

        .empty-icon {
            width: 80px;
            height: 80px;
        }

        .empty-state h3 {
            font-size: 20px;
        }

        .empty-state p {
            font-size: 14px;
        }
    }

    /* ✅ 360px - Çok küçük mobil için ekstra optimizasyon */
    @media (max-width: 360px) {
        .container {
            padding: 0 10px;
        }

        .hero-stats {
            gap: 8px;
        }

        .stat-card {
            padding: 12px;
        }

        .stat-icon {
            width: 36px;
            height: 36px;
        }

        .stat-value {
            font-size: 18px;
        }

        .stat-label {
            font-size: 10px;
        }

        .hero-text h1 {
            font-size: 22px;
        }
    }
  `}</style>;
}